/* eslint-disable @typescript-eslint/no-explicit-any */
// <copyright>
// Copyright by the Spark Development Network
//
// Licensed under the Rock Community License (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.rockrms.com/license
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// </copyright>
//

/**
 * A function that will select a value from the object.
 */
type ValueSelector<T> = (value: T) => string | number | boolean | null | undefined;

/**
 * A function that will perform testing on a value to see if it meets
 * a certain condition and return true or false.
 */
type PredicateFn<T> = (value: T, index: number) => boolean;

/**
 * A function that will compare two values to see which one should
 * be ordered first.
 */
type ValueComparer<T> = (a: T, b: T) => number;

const moreThanOneElement = "More than one element was found in collection.";

const noElementsFound = "No element was found in collection.";

/**
 * Compares the values of two objects given the selector function.
 *
 * For the purposes of a compare, null and undefined are always a lower
 * value - unless both values are null or undefined in which case they
 * are considered equal.
 *
 * @param keySelector The function that will select the value.
 * @param descending True if this comparison should be in descending order.
 */
function valueComparer<T>(keySelector: ValueSelector<T>, descending: boolean): ValueComparer<T> {
    return (a: T, b: T): number => {
        const valueA = keySelector(a);
        const valueB = keySelector(b);

        // If valueA is null or undefined then it will either be considered
        // lower than or equal to valueB.
        if (valueA === undefined || valueA === null) {
            // If valueB is also null or undefined then they are considered equal.
            if (valueB === undefined || valueB === null) {
                return 0;
            }

            return !descending ? -1 : 1;
        }

        // If valueB is undefined or null (but valueA is not) then it is considered
        // a lower value than valueA.
        if (valueB === undefined || valueB === null) {
            return !descending ? 1 : -1;
        }

        // Perform a normal comparison.
        if (valueA > valueB) {
            return !descending ? 1 : -1;
        }
        else if (valueA < valueB) {
            return !descending ? -1 : 1;
        }
        else {
            return 0;
        }
    };
}


/**
 * Provides LINQ style access to an array of elements.
 * @deprecated since version 18.0
 */
export class List<T> {
    /** The elements being tracked by this list. */
    protected elements: T[];

    // #region Constructors

    /**
     * Creates a new list with the given elements.
     *
     * @param elements The elements to be made available to LINQ queries.
     */
    constructor(elements?: T[]) {
        if (elements === undefined) {
            this.elements = [];
        }
        else {
            // Copy the array so if the caller makes changes it won't be reflected by us.
            this.elements = [...elements];
        }
    }

    /**
     * Creates a new List from the elements without copying to a new array.
     *
     * @param elements The elements to initialize the list with.
     * @returns A new list of elements.
     */
    public static fromArrayNoCopy<T>(elements: T[]): List<T> {
        const list = new List<T>();

        list.elements = elements;

        return list;
    }

    // #endregion

    /**
     * Returns a boolean that determines if the collection contains any elements.
     *
     * @returns true if the collection contains any elements; otherwise false.
     */
    public any(): boolean;

    /**
     * Filters the list by the predicate and then returns a boolean that determines
     * if the filtered collection contains any elements.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns true if the collection contains any elements; otherwise false.
     */
    public any(predicate: PredicateFn<T>): boolean;

    /**
     * Filters the list by the predicate and then returns a boolean that determines
     * if the filtered collection contains any elements.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns true if the collection contains any elements; otherwise false.
     */
    public any(predicate?: PredicateFn<T>): boolean {
        let elements = this.elements;

        if (predicate !== undefined) {
            elements = elements.filter(predicate);
        }

        return elements.length > 0;
    }

    /**
     * Returns the first element from the collection if there are any elements.
     * Otherwise will throw an exception.
     *
     * @returns The first element in the collection.
     */
    public first(): T;

    /**
     * Filters the list by the predicate and then returns the first element
     * in the collection if any remain. Otherwise throws an exception.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns The first element in the collection.
     */
    public first(predicate: PredicateFn<T>): T;

    /**
     * Filters the list by the predicate and then returns the first element
     * in the collection if any remain. Otherwise throws an exception.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns The first element in the collection.
     */
    public first(predicate?: PredicateFn<T>): T {
        let elements = this.elements;

        if (predicate !== undefined) {
            elements = elements.filter(predicate);
        }

        if (elements.length >= 1) {
            return elements[0];
        }
        else {
            throw noElementsFound;
        }
    }

    /**
     * Returns the first element found in the collection or undefined if the
     * collection contains no elements.
     *
     * @returns The first element in the collection or undefined.
     */
    public firstOrUndefined(): T | undefined;

    /**
     * Filters the list by the predicate and then returns the first element
     * found in the collection. If no elements remain then undefined is
     * returned instead.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns The first element in the filtered collection or undefined.
     */
    public firstOrUndefined(predicate: PredicateFn<T>): T | undefined;

    /**
     * Filters the list by the predicate and then returns the first element
     * found in the collection. If no elements remain then undefined is
     * returned instead.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns The first element in the filtered collection or undefined.
     */
    public firstOrUndefined(predicate?: PredicateFn<T>): T | undefined {
        let elements = this.elements;

        if (predicate !== undefined) {
            elements = elements.filter(predicate);
        }

        if (elements.length === 1) {
            return elements[0];
        }
        else {
            return undefined;
        }
    }

    /**
     * Returns the last element found in the collection or undefined if the
     * collection contains no elements.
     *
     * @returns The last element in the collection or undefined.
     */
    public lastOrUndefined(): T | undefined;

    /**
     * Filters the list by the predicate and then returns the last element
     * found in the collection. If no elements remain then undefined is
     * returned instead.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns The last element in the filtered collection or undefined.
     */
    public lastOrUndefined(predicate: PredicateFn<T>): T | undefined;

    /**
     * Filters the list by the predicate and then returns the last element
     * found in the collection. If no elements remain then undefined is
     * returned instead.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns The last element in the filtered collection or undefined.
     */
    public lastOrUndefined(predicate?: PredicateFn<T>): T | undefined {
        let elements = this.elements;

        if (predicate !== undefined) {
            elements = elements.filter(predicate);
        }

        if (elements.length) {
            return elements[elements.length - 1];
        }
        else {
            return undefined;
        }
    }

    /**
     * Returns a single element from the collection if there is a single
     * element. Otherwise will throw an exception.
     *
     * @returns An element.
     */
    public single(): T;

    /**
     * Filters the list by the predicate and then returns the single remaining
     * element from the collection. If more than one element remains then an
     * exception will be thrown.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns An element.
     */
    public single(predicate: PredicateFn<T>): T;

    /**
     * Filters the list by the predicate and then returns the single remaining
     * element from the collection. If more than one element remains then an
     * exception will be thrown.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns An element.
     */
    public single(predicate?: PredicateFn<T>): T {
        let elements = this.elements;

        if (predicate !== undefined) {
            elements = elements.filter(predicate);
        }

        if (elements.length === 1) {
            return elements[0];
        }
        else {
            throw moreThanOneElement;
        }
    }

    /**
     * Returns a single element from the collection if there is a single
     * element. If no elements are found then undefined is returned. More
     * than a single element will throw an exception.
     *
     * @returns An element or undefined.
     */
    public singleOrUndefined(): T | undefined;

    /**
     * Filters the list by the predicate and then returns the single element
     * from the collection if there is only one remaining. If no elements
     * remain then undefined is returned. More than a single element will throw
     * an exception.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns An element or undefined.
     */
    public singleOrUndefined(predicate: PredicateFn<T>): T | undefined;

    /**
     * Filters the list by the predicate and then returns the single element
     * from the collection if there is only one remaining. If no elements
     * remain then undefined is returned. More than a single element will throw
     * an exception.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns An element or undefined.
     */
    public singleOrUndefined(predicate?: PredicateFn<T>): T | undefined {
        let elements = this.elements;

        if (predicate !== undefined) {
            elements = elements.filter(predicate);
        }

        if (elements.length === 0) {
            return undefined;
        }
        else if (elements.length === 1) {
            return elements[0];
        }
        else {
            throw moreThanOneElement;
        }
    }

    /**
     * Orders the elements of the array and returns a new list of items
     * in that order.
     *
     * @param keySelector The selector for the key to be ordered by.
     * @returns A new ordered list of elements.
     */
    public orderBy(keySelector: ValueSelector<T>): OrderedList<T> {
        const comparer = valueComparer(keySelector, false);

        return new OrderedList(this.elements, comparer);
    }

    /**
     * Orders the elements of the array in descending order and returns a
     * new list of items in that order.
     *
     * @param keySelector The selector for the key to be ordered by.
     * @returns A new ordered list of elements.
     */
    public orderByDescending(keySelector: ValueSelector<T>): OrderedList<T> {
        const comparer = valueComparer(keySelector, true);

        return new OrderedList(this.elements, comparer);
    }

    /**
     * Filters the results and returns a new list containing only the elements
     * that match the predicate.
     *
     * @param predicate The predicate to filter elements with.
     *
     * @returns A new collection of elements that match the predicate.
     */
    public where(predicate: PredicateFn<T>): List<T> {
        return new List<T>(this.elements.filter(predicate));
    }

    /**
     * Get the elements of this list as a native array of items.
     *
     * @returns An array of items with all filters applied.
     */
    public toArray(): T[] {
        return [...this.elements];
    }
}

/**
 * A list of items that has ordering already applied.
 */
class OrderedList<T> extends List<T> {
    /** The base comparer to use when ordering. */
    private baseComparer!: ValueComparer<T>;

    // #region Constructors

    constructor(elements: T[], baseComparer: ValueComparer<T>) {
        super(elements);

        this.baseComparer = baseComparer;
        this.elements.sort(this.baseComparer);
    }

    // #endregion

    /**
     * Orders the elements of the array and returns a new list of items
     * in that order.
     *
     * @param keySelector The selector for the key to be ordered by.
     * @returns A new ordered list of elements.
     */
    public thenBy(keySelector: ValueSelector<T>): OrderedList<T> {
        const comparer = valueComparer(keySelector, false);

        return new OrderedList(this.elements, (a: T, b: T) => this.baseComparer(a, b) || comparer(a, b));
    }

    /**
     * Orders the elements of the array in descending order and returns a
     * new list of items in that order.
     *
     * @param keySelector The selector for the key to be ordered by.
     * @returns A new ordered list of elements.
     */
    public thenByDescending(keySelector: ValueSelector<T>): OrderedList<T> {
        const comparer = valueComparer(keySelector, true);

        return new OrderedList(this.elements, (a: T, b: T) => this.baseComparer(a, b) || comparer(a, b));
    }
}

/**
 * A utility class for working with iterables in a LINQ-like manner.
 */
export class Enumerable<T> {
    protected iterableFactory: () => Iterable<T>;

    /**
     * Creates an instance of Enumerable using a factory for the iterable.
     * @param iterableFactory - A factory function that produces an iterable.
     */
    constructor(iterableFactory: () => Iterable<T>) {
        this.iterableFactory = iterableFactory;
    }

    /**
     * Creates an Enumerable from a regular iterable (e.g., Array, Set).
     * @param iterable - An iterable to create the Enumerable from.
     * @returns A new Enumerable instance.
     */
    static from<T>(iterable: Iterable<T>): Enumerable<T>;

    /**
     * Creates an Enumerable from a generator function.
     * @param generator - A function that produces an IterableIterator.
     * @returns A new Enumerable instance.
     */
    static from<T>(generator: () => IterableIterator<T>): Enumerable<T>;

    /**
     * Creates an Enumerable from a regular iterable (e.g., Array, Set) or a generator function.
     * @param source - Either an iterable or a generator function.
     * @returns A new Enumerable instance.
     */
    static from<T>(source: Iterable<T> | (() => IterableIterator<T>)): Enumerable<T> {
        if (typeof source === "function") {
            return new Enumerable(source); // Handle generator factory
        }
        else {
            return new Enumerable(() => source); // Handle regular iterable
        }
    }

    /**
     * Returns an iterator for the current Enumerable.
     * @returns An iterator for the iterable.
     */
    *[Symbol.iterator](): Iterator<T> {
        // Regenerate the iterable.
        yield* this.iterableFactory();
    }

    /**
     * Aggregates the elements of the sequence using a specified accumulator function and seed value.
     *
     * This method realizes the enumerable and iterates through each element immediately.
     *
     * Use `scan()` for a lazy version of this method.
     *
     * @param accumulator - A function that accumulates each element.
     * @param seed - The initial value for the accumulation.
     * @returns The aggregated value.
     */
    aggregate<U>(accumulator: (acc: U, item: T, index: number) => U, seed: U): U {
        let result = seed;
        let index = 0;

        for (const item of this) {
            result = accumulator(result, item, index++);
        }
        return result;
    }

    /**
     * Determines whether all elements in the sequence satisfy a condition or if the sequence contains any elements.
     * @param predicate - An optional function to test each element for a condition.
     * @returns `true` if all elements satisfy the condition or if the collection is empty; otherwise, `false`.
     */
    all(predicate: (item: T) => boolean): boolean {
        for (const item of this) {
            if (!predicate(item)) {
                return false;
            }
        }

        return true;
    }

    /**
     * Determines whether any elements in the sequence satisfy a condition or if the sequence contains any elements.
     * @param predicate - An optional function to test each element for a condition.
     * @returns `true` if any elements satisfy the condition; otherwise, `false`.
     */
    any(predicate?: (item: T) => boolean): boolean {
        for (const item of this) {
            if (!predicate || predicate(item)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Computes the average of the projected numeric values in the sequence.
     * @param selector - A function that selects the number from each element.
     * @returns The average of the numbers.
     * @throws Error if no valid numeric elements are found.
     */
    average(selector: (item: T) => number): number {
        let total = 0;
        let count = 0;

        for (const item of this) {
            const value = selector(item);

            if (typeof value === "number" && !isNaN(value)) {
                total += value;
                count++;
            }
        }

        if (count === 0) {
            return 0;
        }
        else {
            return total / count;
        }
    }

    /**
     * Casts the elements of the sequence to the specified type.
     * Performs a runtime type check for each element and throws an
     * error if any element cannot be cast to the target type.
     *
     * @template U The target type to cast the elements to (must extend `T`).
     * @param typeCheck A runtime type check function that verifies if the element is of type `U`.
     * @returns A new Enumerable containing the elements cast to type `U`.
     * @throws Error if any element cannot be cast to the specified type.
     *
     * @example
     * const numbers = Enumerable.from([1, 2, 3])
     *     // .select(...) //some projection that results in unknown type
     *     .cast<number>(x => typeof x === "number"); // cast back to number.
     * console.log(numbers.toArray()); // [1, 2, 3]
     *
     * @example
     * const badCast = Enumerable.from([1, "hello"])
     *     .cast<number>((x): x is number => typeof x === "number");
     * // Throws: Invalid cast: Value 'hello' cannot be cast to the specified type.
     */
    cast<U extends T>(typeCheck: (item: T) => item is U): Enumerable<U> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        // Checked cast: throw when the type check fails.
        return new Enumerable(function* () {
            for (const item of self) {
                if (!typeCheck(item)) {
                    throw new Error(
                        `Invalid cast: Value '${item}' cannot be cast to the specified type.`
                    );
                }
                yield item;
            }
        });
    }

    /**
     * Concatenates the current sequence with another sequence.
     * @param second - The second sequence to concatenate.
     * @returns A new Enumerable containing the concatenated elements.
     */
    concat(second: Iterable<T>): this {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        return this.spawn(function* () {
            yield* self;
            yield* second;
        });
    }

    /**
     * Returns the number of elements in the sequence.
     * @returns The total number of elements.
     */
    count(): number;

    /**
     * Returns the number of elements in the sequence that satisfy the specified predicate.
     * @param predicate A function to test each element.
     * @returns The number of matching elements.
     */
    count(predicate: (item: T) => boolean): number;

    count(predicate?: (item: T) => boolean): number {
        let total = 0;

        for (const item of this) {
            if (!predicate || predicate(item)) {
                total++;
            }
        }

        return total;
    }

    /**
     * Returns distinct elements from the sequence, using strict equality.
     *
     * Works well for primitives (numbers, strings, booleans, symbols)
     *
     * @returns A new Enumerable with unique elements.
     */
    distinct(): this {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        return this.spawn(function* () {
            const seen = new Set<T>();
            for (const item of self) {
                if (!seen.has(item)) {
                    seen.add(item);
                    yield item;
                }
            }
        });
    }

    /**
     * Returns distinct elements from the sequence based on a key selector.
     * @param keySelector A function to extract the key for comparison.
     * @returns A new Enumerable with unique elements by key.
     */
    distinctBy<TKey>(keySelector: (item: T) => TKey): this {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        return this.spawn(function* () {
            const seenKeys = new Set<string>();

            for (const item of self) {
                const key = keySelector(item);
                const keyString = JSON.stringify(key); // acts like a structural hash

                if (!seenKeys.has(keyString)) {
                    seenKeys.add(keyString);
                    yield item;
                }
            }
        });
    }

    /**
     * Returns the first element from the collection if there are any elements.
     * Otherwise will throw an exception.
     *
     * @returns The first element in the collection.
     */
    public first(): T;

    /**
     * Filters the list by the predicate and then returns the first element
     * in the collection if any remain. Otherwise throws an exception.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns The first element in the collection.
     */
    public first(predicate: PredicateFn<T>): T;

    /**
     * Filters the list by the predicate and then returns the first element
     * in the collection if any remain. Otherwise throws an exception.
     *
     * @param predicate The predicate to filter the elements by.
     *
     * @returns The first element in the collection.
     */
    public first(predicate?: PredicateFn<T>): T {
        let i = 0;

        for (const item of this) {
            if (!predicate || predicate(item, i)) {
                return item;
            }

            i++;
        }

        throw noElementsFound;
    }

    /**
     * Returns the first element of the sequence or `undefined` if the sequence is empty.
     * @returns The first element of the sequence or `undefined`.
     */
    firstOrDefault(): T | undefined;

    /**
     * Returns the first element of the sequence or a default value if the sequence is empty.
     * @param defaultValue - The default value to return if the sequence is empty.
     * @returns The first element of the sequence or the default value.
     */
    firstOrDefault(defaultValue: T): T;

    /**
     * Returns the first element of the sequence that satisfies the specified predicate,
     * or `undefined` if no such element is found.
     *
     * @param predicate A function to test each element for a condition.
     * @returns The first element that matches the predicate, or `undefined` if none match.
     *
     * @example
     * const numbers = Enumerable.from([1, 2, 3, 4]);
     * const firstEven = numbers.firstOrDefault(n => n % 2 === 0);
     * console.log(firstEven); // Outputs: 2
     *
     * @example
     * const empty = Enumerable.from<number>([]);
     * const result = empty.firstOrDefault(n => n > 0);
     * console.log(result); // Outputs: undefined
     */
    firstOrDefault(predicate: (item: T) => boolean): T | undefined;

    firstOrDefault(arg?: T | ((item: T) => boolean)): T | undefined {
        if (typeof arg === "function") {
            const predicate = arg as (item: T) => boolean;

            for (const item of this) {
                if (predicate(item)) {
                    return item;
                }
            }
        }
        else {
            for (const item of this) {
                return item;
            }

            return arg;
        }
    }

    /**
     * Executes a specified action for each element in the sequence.
     * @param action - A function to execute for each element.
     */
    forEach(action: (item: T) => void): void {
        for (const item of this) {
            action(item);
        }
    }

    /**
     * Groups the elements of a sequence according to a specified key selector function.
     * Each grouping is an Enumerable with a `.key` property.
     *
     * @param keySelector A function to extract the key for each element.
     * @returns A new Enumerable of groupings.
     */
    groupBy<TKey>(keySelector: (item: T) => TKey): Enumerable<GroupedEnumerable<TKey, T>> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        return new Enumerable(function* () {
            const map = new Map<string, { key: TKey, values: T[] }>();

            for (const item of self) {
                const key = keySelector(item);
                const keyString = JSON.stringify(key); // acts like GetHashCode + ToString()

                const entry = map.get(keyString);

                if (entry) {
                    entry.values.push(item);
                }
                else {
                    map.set(keyString, { key, values: [item] });
                }
            }

            for (const { key, values } of map.values()) {
                yield new GroupedEnumerable<TKey, T>(key, values);
            }
        });
    }

    /**
     * Returns the last element of the sequence.
     * @returns The last element.
     * @throws If the sequence is empty.
     */
    last(): T;

    /**
     * Returns the last element of the sequence that satisfies the predicate.
     * @param predicate A function to test each element.
     * @returns The last matching element.
     * @throws If no matching element is found.
     */
    last(predicate: (item: T) => boolean): T;

    last(predicate?: (item: T) => boolean): T {
        let found = false;
        let lastMatch: T | undefined = undefined;

        for (const item of this) {
            if (!predicate || predicate(item)) {
                found = true;
                lastMatch = item;
            }
        }

        if (!found || lastMatch === undefined) {
            throw new Error("No element satisfies the condition in last()");
        }

        return lastMatch;
    }

    /**
     * Returns the last element of the sequence, or `undefined` if the sequence is empty.
     * @returns The last element of the sequence, or `undefined`.
     *
     * @example
     * const numbers = Enumerable.from([1, 2, 3]);
     * console.log(numbers.lastOrDefault()); // Outputs: 3
     *
     * @example
     * const empty = Enumerable.from<number>([]);
     * console.log(empty.lastOrDefault()); // Outputs: undefined
     */
    lastOrDefault(): T | undefined;

    /**
     * Returns the last element of the sequence, or a default value if the sequence is empty.
     * @param defaultValue - The default value to return if the sequence is empty.
     * @returns The last element of the sequence, or the provided default value.
     *
     * @example
     * const numbers = Enumerable.from([1, 2, 3]);
     * console.log(numbers.lastOrDefault()); // Outputs: 3
     *
     * @example
     * const empty = Enumerable.from<number>([]);
     * console.log(empty.lastOrDefault(0)); // Outputs: 0
     */
    lastOrDefault(defaultValue: T): T;

    /**
     * Returns the last element in the sequence that satisfies the given predicate,
     * or `undefined` if no such element is found.
     *
     * @param predicate A function to test each element.
     * @returns The last matching element, or `undefined`.
     *
     * @example
     * const nums = Enumerable.from([1, 2, 3, 4]);
     * console.log(nums.lastOrDefault(x => x % 2 === 0)); // 4
     */
    lastOrDefault(predicate: (item: T) => boolean): T | undefined;

    lastOrDefault(arg?: T | ((item: T) => boolean)): T | undefined {
        let last: T | undefined;

        if (typeof arg === "function") {
            const predicate = arg as ((item: T) => boolean);

            for (const item of this) {
                // Update `last` for each element that satisfies the predicate.
                if (predicate(item)) {
                    last = item;
                }
            }
        }
        else {
            for (const item of this) {
                // Update `last` for each element.
                last = item;
            }
        }

        return last;
    }

    /**
     * Returns the maximum element of the sequence.
     *
     * @template T The type of the elements in the sequence.
     * @param selector A function to project each element to a numeric, comparable value.
     * @returns The maximum element or `undefined` if the sequence is empty.
     *
     * @example
     * Enumerable.from([3, 7, 2, 9]).max(); // 9
     * Enumerable.from([{x: 1}, {x: 5}]).max(e => e.x); // 5
     */
    max(selector: (item: T) => number): number | undefined {
        const self = this[Symbol.iterator]();
        const first = self.next();
        if (first.done) return undefined;

        let maxItem = selector(first.value);

        for (let next = self.next(); !next.done; next = self.next()) {
            const value = selector(next.value);
            if (value > maxItem) {
                maxItem = value;
            }
        }

        return maxItem;
    }

    /**
     * Returns the minimum element of the sequence.
     *
     * @template T The type of the elements in the sequence.
     * @param selector A function to project each element to a numeric, comparable value.
     * @returns The minimum element or `undefined` if the sequence is empty.
     *
     * @example
     * Enumerable.from([{x: 1}, {x: 5}]).min(e => e.x); // 1
     */
    min(selector: (item: T) => number): number | undefined {
        const self = this[Symbol.iterator]();
        const first = self.next();
        if (first.done) return undefined;

        let minItem = selector(first.value);

        for (let next = self.next(); !next.done; next = self.next()) {
            const value = selector(next.value);
            if (value < minItem) {
                minItem = value;
            }
        }

        return minItem;
    }

    /**
     * Returns the maximum element of the sequence.
     *
     * @template T The type of the elements in the sequence.
     * @param selector A function to project each element to a numeric, comparable value.
     * @returns The maximum element or `undefined` if the sequence is empty.
     *

        let maxItem = selector(first.value);

        for (let next = self.next(); !next.done; next = self.next()) {
            const value = selector(next.value);
            if (value > maxItem) {
                maxItem = value;
            }
        }

        return maxItem;
    }

    /**
     * Filters the sequence and returns only elements of the specified type.
     * @template U The target type to filter by.
     * @param typeCheck - A runtime check function to validate the type of each element.
     * @returns A new Enumerable containing elements of type `U`.
     *
     * @example
     * const mixed = Enumerable.from([1, "hello", true, 42]);
     * const numbers = mixed.ofType<number>(item => typeof item === "number");
     * console.log(numbers.toArray()); // Outputs: [1, 42]
     */
    ofType<U extends T>(typeCheck: (item: T) => item is U): Enumerable<U> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return new Enumerable(function* () {
            for (const item of self) {
                if (typeCheck(item)) {
                    yield item;
                }
            }
        });
    }

    /**
     * Sorts the elements of the sequence in ascending order.
     * @param keySelector - Function to extract the key for comparison.
     * @param comparer - Optional comparison function for the keys.
     * @returns An OrderedEnumerable sorted in ascending order.
     */
    orderBy<U>(
        keySelector: (item: T) => U,
        comparer: (a: U, b: U) => number = (a, b) => (a > b ? 1 : a < b ? -1 : 0)
    ): OrderedEnumerable<T> {
        return new OrderedEnumerable(
            this.iterableFactory,
            (a, b) => comparer(keySelector(a), keySelector(b))
        );
    }

    /**
     * Sorts the elements of the sequence in descending order.
     * @param keySelector - Function to extract the key for comparison.
     * @param comparer - Optional comparison function for the keys.
     * @returns An OrderedEnumerable sorted in descending order.
     */
    orderByDescending<U>(
        keySelector: (item: T) => U,
        comparer: (a: U, b: U) => number = (a, b) => (a > b ? 1 : a < b ? -1 : 0)
    ): OrderedEnumerable<T> {
        const descendingComparer = (a: U, b: U): number => -comparer(a, b);
        return this.orderBy(keySelector, descendingComparer);
    }

    /**
     * Returns a new sequence with the specified element(s) prepended to the start.
     *
     * @template T The type of elements in the sequence.
     * @param items One or more elements to prepend.
     * @returns A new Enumerable with the items inserted before the existing sequence.
     *
     * @example
     * const nums = Enumerable.from([2, 3]);
     * const withOne = nums.prepend(1);
     * console.log(withOne.toArray()); // [1, 2, 3]
     *
     * @example
     * const moreNums = nums.prepend(-1, 0, 1);
     * console.log(moreNums.toArray()); // [-1, 0, 1, 2, 3]
     */
    prepend(items: Iterable<T>): this {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        return this.spawn(function* () {
            for (const item of items) {
                yield item;
            }
            for (const item of self) {
                yield item;
            }
        });
    }

    /**
     * Applies an accumulator function over the sequence, yielding each intermediate result.
     * The first yielded value is based on the seed provided.
     *
     * This behaves like a "scan" or "running total" operation in functional programming.
     * It is the lazy counterpart to `aggregate()` that preserves intermediate state at each step.
     *
     * @template U The type of the accumulated value.
     * @param seed The initial accumulator value.
     * @param accumulator A function that takes the accumulated value, the current element, and the index,
     *                    and returns the new accumulated value.
     * @returns An Enumerable of the intermediate accumulated values.
     *
     * @example
     * const values = Enumerable.from([1, 2, 3, 4])
     *     .scan(0, (acc, val) => acc + val)
     *     .toArray();
     * // Output: [1, 3, 6, 10]
     *
     * @example
     * const withIndex = Enumerable.from(["a", "b", "c"])
     *     .scan([], (acc, item, index) => [...acc, `${index}:${item}`])
     *     .toArray();
     * // Output: [["0:a"], ["0:a", "1:b"], ["0:a", "1:b", "2:c"]]
     */
    scan<U>(seed: U, accumulator: (acc: U, item: T, index: number) => U): Enumerable<U> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        return new Enumerable<U>(function* () {
            let acc = seed;
            let index = 0;

            for (const item of self) {
                acc = accumulator(acc, item, index++);
                yield acc;
            }
        });
    }

    /**
     * Projects each element of the sequence into a new form.
     * @param selector - A function to project each element into a new form.
     * @returns A new Enumerable with the projected elements.
     */
    select<U>(selector: (item: T) => U): Enumerable<U> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        return new Enumerable(function* () {
            for (const item of self) {
                yield selector(item);
            }
        });
    }

    /**
     * Projects each element of the sequence to an iterable and flattens the resulting sequences into one sequence.
     * @param collectionSelector A transform function to apply to each element.
     * @returns A new Enumerable containing the flattened results.
     */
    selectMany<U>(collectionSelector: (item: T) => Iterable<U>): Enumerable<U> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        return new Enumerable(function* () {
            for (const item of self) {
                const collection = collectionSelector(item);
                for (const subItem of collection) {
                    yield subItem;
                }
            }
        });
    }

    /**
     * Determines whether two sequences are equal by comparing their elements using
     * strict equality (`===`) or an optional equality comparer.
     *
     * @param other The other sequence to compare to.
     * @param comparer Optional equality comparer function. Defaults to strict equality.
     * @returns `true` if both sequences have the same length and all elements are equal; otherwise, `false`.
     *
     * @example
     * const seq1 = Enumerable.from([1, 2, 3]);
     * const seq2 = Enumerable.from([1, 2, 3]);
     * console.log(seq1.sequenceEqual(seq2)); // Outputs: true
     *
     * @example
     * const seq3 = Enumerable.from(["a", "b", "c"]);
     * const seq4 = Enumerable.from(["a", "B", "c"]);
     * console.log(seq3.sequenceEqual(seq4, (a, b) => a.toLowerCase() === b.toLowerCase())); // Outputs: true
     */
    sequenceEqual(
        other: Iterable<T>,
        comparer: (a: T, b: T) => boolean = (a, b) => a === b
    ): boolean {
        const it1 = this[Symbol.iterator]();
        const it2 = other[Symbol.iterator]();

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const n1 = it1.next();
            const n2 = it2.next();

            if (n1.done && n2.done) return true;  // Both ended → equal
            if (n1.done !== n2.done) return false; // One ended first → not equal
            if (!comparer(n1.value, n2.value)) return false; // Mismatch → not equal
        }
    }


    /**
     * Returns a new Enumerable that skips the first `count` elements of the sequence.
     * @param count - The number of elements to skip.
     * @returns A new Enumerable that skips the specified number of elements.
     */
    skip(count: number): this {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return this.spawn(function* () {
            let skipped = 0;
            for (const item of self) {
                if (skipped++ >= count) {
                    yield item;
                }
            }
        });
    }

    /**
     * Recreates an instance of the *same runtime class*.
     * This allows subtype method chaining to return the subtype instead of Enumerable<T>.
     */
    protected spawn(factory: () => Iterable<T>): this {
        const ctor = this.constructor as new (f: () => Iterable<T>) => this;
        return new ctor(factory);
    }

    /**
     * Computes the sum of the projected numeric values in the list.
     * @param selector - A function that selects the number from each element.
     * @returns The sum of the numbers.
     */
    sum(selector: (item: T) => number): number {
        let total = 0;

        for (const item of this) {
            const value = selector(item);

            if (typeof value === "number" && !isNaN(value)) {
                total += value;
            }
        }

        return total;
    }

    /**
     * Returns a new Enumerable that contains the first `count` elements of the sequence.
     * @param count - The number of elements to take.
     * @returns A new Enumerable containing the taken elements.
     */
    take(count: number): Enumerable<T> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return this.spawn(function* () {
            let i = 0;
            for (const item of self) {
                if (i++ < count) {
                    yield item;
                }
                else {
                    break;
                }
            }
        });
    }

    /**
     * Converts the sequence into an array.
     * @returns An array containing all elements in the sequence.
     */
    toArray(): T[] {
        return Array.from(this);
    }

    /**
     * Converts the sequence into a Map using a key selector and optional value selector.
     * @param keySelector - A function to extract the key from each element.
     * @param valueSelector - A function to extract the value from each element. Defaults to the item itself.
     * @returns A Map of key-value pairs.
     */
    toDictionary<TKey, TValue = T>(
        keySelector: (item: T) => TKey,
        valueSelector?: (item: T) => TValue
    ): Map<TKey, TValue> {
        return new Map<TKey, TValue>(
            this.select<[TKey, TValue]>(item => [
                keySelector(item),
                valueSelector ? valueSelector(item) : (item as unknown as TValue)
            ])
        );
    }

    /**
     * Filters the sequence to include only elements that satisfy the predicate.
     * @param predicate - A function to test each element for a condition.
     * @returns A new Enumerable containing the filtered elements.
     */
    where(predicate: (item: T) => boolean): Enumerable<T> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return this.spawn(function* (): Generator<T, void, unknown> {
            for (const item of self) {
                if (predicate(item)) {
                    yield item;
                }
            }
        });
    }

    /**
     * Returns a new Enumerable that yields each element of the sequence paired with its index.
     *
     * @returns An Enumerable of [element, index] tuples.
     *
     * @example
     * // Example usage with a for...of loop:
     * const elements = Enumerable.from(['a', 'b', 'c']);
     * for (const [item, index] of elements.withIndex()) {
     *     console.log(`Index: ${index}, Item: ${item}`);
     * }
     * // Output:
     * // Index: 0, Item: a
     * // Index: 1, Item: b
     * // Index: 2, Item: c
     *
     * @example
     * // Example usage with chaining:
     * const indexed = Enumerable.from(['x', 'y', 'z'])
     *     .withIndex()
     *     .where(([item, index]) => index % 2 === 0)
     *     .toArray();
     * console.log(indexed);
     * // Output: [['x', 0], ['z', 2]]
     */
    withIndex(): Enumerable<[T, number]> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        return new Enumerable(function* (): Generator<[T, number]> {
            let index = 0;
            for (const item of self) {
                yield [item, index++];
            }
        });
    }
}

class OrderedEnumerable<T> extends Enumerable<T> {
    private readonly sortComparers: ((a: T, b: T) => number)[];

    constructor(
        iterableFactory: () => Iterable<T>,
        initialComparer: (a: T, b: T) => number
    ) {
        super(iterableFactory);
        this.sortComparers = [initialComparer];
    }

    /**
     * Adds a secondary ascending order comparison to the current sort.
     * @param keySelector - Function to extract the key for comparison.
     * @param comparer - Optional comparison function for the keys.
     * @returns A new OrderedEnumerable with the additional ordering.
     */
    thenOrderBy<U>(
        keySelector: (item: T) => U,
        comparer: (a: U, b: U) => number = (a, b) => (a > b ? 1 : a < b ? -1 : 0)
    ): OrderedEnumerable<T> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return new OrderedEnumerable(
            this.iterableFactory,
            (a, b) => {
                for (const cmp of self.sortComparers) {
                    const result = cmp(a, b);
                    if (result !== 0) return result;
                }
                return comparer(keySelector(a), keySelector(b));
            }
        );
    }

    /**
     * Adds a secondary descending order comparison to the current sort.
     * @param keySelector - Function to extract the key for comparison.
     * @param comparer - Optional comparison function for the keys.
     * @returns A new OrderedEnumerable with the additional ordering.
     */
    thenOrderByDescending<U>(
        keySelector: (item: T) => U,
        comparer: (a: U, b: U) => number = (a, b) => (a > b ? 1 : a < b ? -1 : 0)
    ): OrderedEnumerable<T> {
        const descendingComparer = (a: U, b: U): number => -comparer(a, b);
        return this.thenOrderBy(keySelector, descendingComparer);
    }

    override toArray(): T[] {
        return Array.from(this);
    }

    /**
     * Sorts the sequence based on the defined comparers.
     * @returns A new Iterable with elements sorted.
     */
    override *[Symbol.iterator](): Iterator<T> {
        const array = Array.from(this.iterableFactory());
        array.sort((a, b) => {
            for (const comparer of this.sortComparers) {
                const result = comparer(a, b);
                if (result !== 0) return result;
            }
            return 0;
        });
        yield* array;
    }
}

export class GroupedEnumerable<TKey, TElement> extends Enumerable<TElement> {
    constructor(public readonly key: TKey, elements: Iterable<TElement>
    ) {
        super(() => elements);
    }
}